import { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "./storeHook";
import { IntegrationAction } from "../actions";
import {
  Customer,
  ActionPermissionUpdate,
  ActionConfigUpdate,
  ApplicationStateUpdate,
  CustomerApplicationSettings,
  AuditLogParams,
} from "../types/integration";
import IntegrationApi from "../apis/integrationApi";
import { ActionTypes } from "../store/actionTypes";
import { useUserMsp } from "./useUserMsp";
import { getApiErrorMessage } from "../utils/apiErrorMessage";

const integrationApi = new IntegrationApi();

export function useIntegration() {
  const dispatch = useAppDispatch();
  const integrationState = useAppSelector((state) => state.integration);

  // Customer Management
  const getCustomers = useCallback(() => {
    dispatch(IntegrationAction.getCustomers());
  }, [dispatch]);

  const getCustomerById = useCallback(
    (customerId: string) => {
      dispatch(IntegrationAction.getCustomerById(customerId));
    },
    [dispatch]
  );

  const createCustomer = useCallback(
    (data: Partial<Customer>) => {
      dispatch(IntegrationAction.createCustomer(data));
    },
    [dispatch]
  );

  const updateCustomer = useCallback(
    (customerId: string, data: Partial<Customer>) => {
      dispatch(IntegrationAction.updateCustomer(customerId, data));
    },
    [dispatch]
  );

  const deleteCustomer = useCallback(
    (customerId: string) => {
      dispatch(IntegrationAction.deleteCustomer(customerId));
    },
    [dispatch]
  );

  const getCustomersByMspId = useCallback(
    (mspId: string) => {
      dispatch(IntegrationAction.getCustomersByMspId(mspId));
    },
    [dispatch]
  );

  // User Management
  const getUsersByMspId = useCallback(
    (mspId: string) => {
      return dispatch(IntegrationAction.getUsersByMspId(mspId));
    },
    [dispatch]
  );

  const createUser = useCallback(
    (data: any) => {
      return dispatch(IntegrationAction.createUser(data));
    },
    [dispatch]
  );

  const updateUser = useCallback(
    (userId: string, data: any) => {
      return dispatch(IntegrationAction.updateUser(userId, data));
    },
    [dispatch]
  );

  const deleteUser = useCallback(
    (userId: string) => {
      return dispatch(IntegrationAction.deleteUser(userId));
    },
    [dispatch]
  );

  // Application Management
  const getApplicationsByCustomer = useCallback(
    (customerId: string) => {
      dispatch(IntegrationAction.getApplicationsByCustomer(customerId));
    },
    [dispatch]
  );

  const getApplicationDetails = useCallback(
    (applicationId: string) => {
      dispatch(IntegrationAction.getApplicationDetails(applicationId));
    },
    [dispatch]
  );

  const getApplicationPermissions = useCallback(
    (applicationId: string) => {
      dispatch(IntegrationAction.getApplicationPermissions(applicationId));
    },
    [dispatch]
  );

  const updateApplicationState = useCallback(
    (params: ApplicationStateUpdate) => {
      return dispatch(IntegrationAction.updateApplicationState(params));
    },
    [dispatch]
  );

  const updateApplicationConfig = useCallback(
    async (
      customerId: string,
      applicationId: string,
      config: Record<string, string>
    ) => {
      try {
        const response = await integrationApi.updateApplicationConfig(
          customerId,
          applicationId,
          config
        );
        dispatch({
          type: ActionTypes.UPDATE_APPLICATION_CONFIG,
          payload: response,
        });
        return response;
      } catch (error: any) {
        dispatch({
          type: ActionTypes.INTEGRATION_ERROR,
          payload: error.message,
        });
        throw error;
      }
    },
    [dispatch]
  );

  const testAndEnableM365Actions = useCallback(async (customerId: string) => {
    return integrationApi.testAndEnableM365Actions(customerId);
  }, []);

  const testApplicationConfiguration = useCallback(
    async (
      customerId: string,
      appName: string,
      config: Record<string, string>
    ) => {
      try {
        const response = await integrationApi.testApplicationConfiguration(
          customerId,
          appName,
          config
        );

        // Store the test result in Redux state with composite key
        dispatch({
          type: ActionTypes.SET_TEST_RESULT,
          payload: {
            customerId,
            applicationId: appName,
            result: response,
          },
        });

        return response;
      } catch (error: unknown) {
        const msg = getApiErrorMessage(error, "Test failed");
        if (error && typeof error === "object" && "message" in error) {
          (error as { message: string }).message = msg;
        }
        dispatch({
          type: ActionTypes.SET_TEST_RESULT,
          payload: {
            customerId,
            applicationId: appName,
            result: {
              success: false,
              message: msg,
            },
          },
        });
        throw error;
      }
    },
    [dispatch]
  );

  const testPlatformConfiguration = useCallback(
    async (
      customerId: string,
      platformName: string,
      config: Record<string, string>
    ) => {
      try {
        const response = await integrationApi.testPlatformConfiguration(
          customerId,
          platformName,
          config
        );

        // Store the test result in Redux state with composite key
        dispatch({
          type: ActionTypes.SET_TEST_RESULT,
          payload: {
            customerId,
            applicationId: platformName,
            result: response,
          },
        });

        return response;
      } catch (error: unknown) {
        const msg = getApiErrorMessage(error, "Test failed");
        const errorData = (error as any)?.response?.data;
        const checks = Array.isArray(errorData?.checks) ? errorData.checks : undefined;
        if (error && typeof error === "object" && "message" in error) {
          (error as { message: string }).message = msg;
        }
        dispatch({
          type: ActionTypes.SET_TEST_RESULT,
          payload: {
            customerId,
            applicationId: platformName,
            result: {
              success: false,
              message: msg,
              ...(checks ? { checks } : {}),
            },
          },
        });
        throw error;
      }
    },
    [dispatch]
  );

  const clearTestResults = useCallback(
    (customerId?: string) => {
      if (customerId) {
        // Clear test results for specific customer
        dispatch({
          type: ActionTypes.SET_TEST_RESULT,
          payload: {
            customerId,
            clearForCustomer: true,
          },
        });
      } else {
        // Clear all test results
        dispatch({
          type: ActionTypes.SET_TEST_RESULT,
          payload: {
            clearAll: true,
          },
        });
      }
    },
    [dispatch]
  );

  /** Drop one cached test outcome (e.g. `${customerId}:${platformName}`) when credentials change. */
  const clearTestResultEntry = useCallback(
    (customerId: string, applicationOrPlatformName: string) => {
      dispatch({
        type: ActionTypes.SET_TEST_RESULT,
        payload: {
          clearCompositeKey: `${customerId}:${applicationOrPlatformName}`,
        },
      });
    },
    [dispatch]
  );

  const addApplicationToCustomer = useCallback(
    (params: CustomerApplicationSettings) => {
      dispatch(IntegrationAction.addApplicationToCustomer(params));
    },
    [dispatch]
  );

  const removeApplicationFromCustomer = useCallback(
    (customerId: string, applicationId: string) => {
      dispatch(
        IntegrationAction.removeApplicationFromCustomer(
          customerId,
          applicationId
        )
      );
    },
    [dispatch]
  );

  // Action Management
  const getApplicationActions = useCallback(
    (customerId: string, applicationId: string) => {
      dispatch(
        IntegrationAction.getApplicationActions(customerId, applicationId)
      );
    },
    [dispatch]
  );

  const clearApplicationActions = useCallback(
    () => {
      dispatch(IntegrationAction.clearApplicationActions());
    },
    [dispatch]
  );

  const updateActionPermission = useCallback(
    (params: ActionPermissionUpdate) => {
      return dispatch(IntegrationAction.updateActionPermission(params));
    },
    [dispatch]
  );

  const updateActionConfig = useCallback(
    (params: ActionConfigUpdate) => {
      return dispatch(IntegrationAction.updateActionConfig(params));
    },
    [dispatch]
  );

  // Platform Management
  const getPlatforms = useCallback(
    (mspId?: string) => {
      dispatch(IntegrationAction.getPlatforms(mspId));
    },
    [dispatch]
  );

  const getPlatformActions = useCallback(
    (platformId: string) => {
      dispatch(IntegrationAction.getPlatformActions(platformId));
    },
    [dispatch]
  );

  const updatePlatformActionPermission = useCallback(
    (platformId: string, actionId: string, permission: string) => {
      return dispatch(
        IntegrationAction.updatePlatformActionPermission(
          platformId,
          actionId,
          permission
        )
      );
    },
    [dispatch]
  );

  const getPlatformConfig = useCallback(
    async (platformId: string) => {
      try {
        const response = await integrationApi.getPlatformConfig(platformId);
        return response;
      } catch (error: any) {
        throw error;
      }
    },
    []
  );

  const updatePlatformConfig = useCallback(
    (platformId: string, config: object) => {
      return dispatch(
        IntegrationAction.updatePlatformConfig(platformId, config)
      );
    },
    [dispatch]
  );

  const updatePlatformState = useCallback(
    (platformId: string, enabled: boolean) => {
      return dispatch(
        IntegrationAction.updatePlatformState(platformId, enabled)
      );
    },
    [dispatch]
  );

  const createPlatform = useCallback(
    async (mspId: string | number, data: any) => {
      try {
        const response = await dispatch(IntegrationAction.createPlatform(mspId, data));
        return response;
      } catch (error: any) {
        throw error;
      }
    },
    [dispatch]
  );

  const createApplication = useCallback(
    async (data: any) => {
      try {
        const response = await dispatch(IntegrationAction.createApplication(data));
        return response;
      } catch (error: any) {
        throw error;
      }
    },
    [dispatch]
  );

  const getAvailablePlatforms = useCallback(
    async () => {
      try {
        const response = await dispatch(IntegrationAction.getAvailablePlatforms());
        return response;
      } catch (error: any) {
        throw error;
      }
    },
    [dispatch]
  );

  const getAvailableApplications = useCallback(
    async () => {
      try {
        const response = await dispatch(IntegrationAction.getAvailableApplications());
        return response;
      } catch (error: any) {
        throw error;
      }
    },
    [dispatch]
  );

  // MSP Management
  const getMsps = useCallback(() => {
    dispatch(IntegrationAction.getMsps());
  }, [dispatch]);

  const getMspById = useCallback(
    (mspId: string) => {
      dispatch(IntegrationAction.getMspById(mspId));
    },
    [dispatch]
  );

  // Statistics and Audit
  const getApplicationStats = useCallback(
    (customerId: string) => {
      dispatch(IntegrationAction.getApplicationStats(customerId));
    },
    [dispatch]
  );

  const getPlatformIntegrationStatus = useCallback(
    (customerId: string) => {
      dispatch(IntegrationAction.getPlatformIntegrationStatus(customerId));
    },
    [dispatch]
  );

  const getActionAuditLog = useCallback(
    (params: AuditLogParams) => {
      dispatch(IntegrationAction.getActionAuditLog(params));
    },
    [dispatch]
  );

  return {
    // State
    ...integrationState,

    // Customer Management
    getCustomers,
    getCustomerById,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomersByMspId,

    // User Management
    users: integrationState.users,
    getUsersByMspId,
    createUser,
    updateUser,
    deleteUser,

    // MSP Management
    msps: integrationState.msps,
    selectedMsp: integrationState.selectedMsp,
    getMsps,
    getMspById,

    // Application Management
    getApplicationsByCustomer,
    getApplicationDetails,
    getApplicationPermissions,
    updateApplicationState,
    updateApplicationConfig,
    testAndEnableM365Actions,
    testApplicationConfiguration,
    testPlatformConfiguration,
    clearTestResults,
    clearTestResultEntry,
    addApplicationToCustomer,
    removeApplicationFromCustomer,

    // Action Management
    getApplicationActions,
    clearApplicationActions,
    updateActionPermission,
    updateActionConfig,

    // Platform Management
    getPlatforms,
    getPlatformActions,
    getPlatformConfig,
    updatePlatformActionPermission,
    updatePlatformConfig,
    updatePlatformState,
    createPlatform,
    createApplication,
    getAvailablePlatforms,
    getAvailableApplications,

    // Statistics and Audit
    getApplicationStats,
    getPlatformIntegrationStatus,
    getActionAuditLog,
  };
}

// Specific hooks for different integration features
export function useCustomers() {
  const {
    customers,
    selectedCustomer,
    isLoading,
    error,
    getCustomers,
    getCustomerById,
    createCustomer,
    updateCustomer,
  } = useIntegration();

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  return {
    customers,
    selectedCustomer,
    isLoading,
    error,
    getCustomerById,
    createCustomer,
    updateCustomer,
  };
}

export function useApplications(customerId?: string) {
  const {
    applications,
    applicationsLoading,
    applicationsLoaded,
    applicationsError,
    selectedApplication,
    error,
    getApplicationsByCustomer,
    getApplicationDetails,
    updateApplicationState,
  } = useIntegration();

  useEffect(() => {
    if (customerId) {
      getApplicationsByCustomer(customerId);
    }
  }, [customerId, getApplicationsByCustomer]);

  return {
    applications,
    selectedApplication,
    isLoading: applicationsLoading,
    applicationsLoading,
    applicationsLoaded,
    error: applicationsError || error,
    applicationsError,
    getApplicationsByCustomer,
    getApplicationDetails,
    updateApplicationState,
  };
}

export function useApplicationActions(
  customerId?: string,
  applicationId?: string
) {
    const {
        applicationActions,
        applicationActionsLoading,
        applicationActionsLoaded,
        applicationActionsError,
        error,
        getApplicationActions,
        updateActionPermission,
        updateActionConfig,
    } = useIntegration();

  useEffect(() => {
    if (customerId && applicationId) {
      getApplicationActions(customerId, applicationId);
    }
  }, [customerId, applicationId, getApplicationActions]);

  return {
    applicationActions,
    isLoading: applicationActionsLoading,
    applicationActionsLoading,
    applicationActionsLoaded,
    error: applicationActionsError || error,
    applicationActionsError,
    updateActionPermission,
    updateActionConfig,
  };
}

export function usePlatforms() {
  const {
    platforms,
    platformsLoading,
    platformsLoaded,
    platformsError,
    platformActions,
    platformActionsLoading,
    platformActionsLoaded,
    platformActionsError,
    error,
    getPlatforms,
    getPlatformActions,
    getPlatformConfig,
    updatePlatformActionPermission,
    testPlatformConfiguration,
    updatePlatformConfig,
    updatePlatformState,
  } = useIntegration();
  const { userMspInfo } = useUserMsp();
  const msp_id = userMspInfo?.msp_id;

  useEffect(() => {
    if (msp_id) {
      getPlatforms(msp_id);
    }
  }, [getPlatforms, msp_id]);

  return {
    platforms,
    platformActions,
    isLoading: platformsLoading,
    platformsLoading,
    platformsLoaded,
    platformActionsLoading,
    platformActionsLoaded,
    error: platformsError || platformActionsError || error,
    platformsError,
    platformActionsError,
    getPlatforms,
    getPlatformActions,
    getPlatformConfig,
    updatePlatformActionPermission,
    testPlatformConfiguration,
    updatePlatformConfig,
    updatePlatformState,
  };
}
